import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Box, Button, Divider, Grid, Stack } from '@mui/material';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage, showAlertWarning } from '@utils/Alerts/Message';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { setConfirmDialog } from '@store/appStore';
import { Input } from '@api/Inputs/Input';
import { reserveShortId } from '@api/Orders/ShortId';
import InputsService from '@api/Inputs/InputCtrl';
import InventoryOrderService from '@api/InventoryOrders/InventoryOrderCtrl';
import { InventoryOrder, InventoryOrderItem, InventoryOrderStatus } from '@api/InventoryOrders/InventoryOrder';
import { ModalCreditCardCalculator } from '@components/Collaborator/Modals/ModalCreditCardCalculator';
import useOpenModal from '@hooks/useOpenModal';
import InputCategoriesTabs from '@screens/BranchOffices/components/InputCategoriesTabs';
import InputsGrid from '@screens/BranchOffices/components/InputsGrid';
import BranchOfficeInventoryService from '@api/BranchOfficeInventories/BranchOfficeInventoryCtrl';
import { getAvailableUnits } from '@api/BranchOfficeInventories/BranchOfficeInventory';
import InventoryCartList from '@screens/BranchOffices/components/InventoryCartList';
import InventoryOrderActions from '@screens/BranchOffices/components/InventoryOrderActions';
import UserServ from '@api/Users/UserServ';

interface OrderItem extends InventoryOrderItem {
    id: string;
}

const InventoryPOS: React.FunctionComponent = () => {
    const location = useLocation();
    const navigation = useNavigate();
    const dispatch = useDispatch();
    const { branchOfficeSelected, distributionCenterSelected } = useSelector((state: RootReducer) => state.auth);
    const { branchOffices } = useSelector((state: RootReducer) => state.krispis);

    const inputsService = useMemo(() => new InputsService(), []);
    const inventoryOrderService = useMemo(() => new InventoryOrderService(), []);

    const [inputs, setInputs] = useState<Input[]>([]);
    // Stock actual de la sucursal, para decidir QUÉ pedir (2026-08-19): las
    // sucursales ya solo tienen renglón de lo que manejan, así que la lista de
    // inventario dejó de servir como "de qué me estoy quedando sin"
    const [stockByInput, setStockByInput] = useState<Map<string, { totalUnits: number; unit: string }>>(new Map());
    const branchOfficeInventoryService = useMemo(() => new BranchOfficeInventoryService(), []);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined);

    const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
    const [note, setNote] = useState<string>('');
    const [currentOrder, setCurrentOrder] = useState<InventoryOrder | undefined>(undefined);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [selectedBranchOfficeId, setSelectedBranchOfficeId] = useState<string | undefined>(undefined);
    const [showBranchSelector, setShowBranchSelector] = useState<boolean>(false);

    const [cashAmount, setCashAmount] = useState<number>(0);
    const [creditCardAmount, setCreditCardAmount] = useState<number>(0);
    const [bankTransferAmount, setBankTransferAmount] = useState<number>(0);

    const modalCashCalculator = useOpenModal();
    const modalCreditCardCalculator = useOpenModal();
    const modalBankTransferCalculator = useOpenModal();
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (isEditing && currentOrder?.id && orderItems.length > 0) {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }

            saveTimeoutRef.current = setTimeout(async () => {
                try {
                    const items = orderItems.map(({ id, ...item }) => item);
                    await inventoryOrderService.updateInventoryOrderItems(currentOrder.id!, items);
                } catch (error: any) {
                    showAlertError(error);
                }
            }, 1000); // 1 second debounce
        }

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [orderItems, isEditing, currentOrder?.id, inventoryOrderService]);

    const loadData = async () => {
        showLoader();
        try {
            // Obtenemos los insumos aquí (las categorías se manejan en su propio componente)
            const inputsData = await inputsService.getInputs();
            setInputs(inputsData);

            const state = location.state as
                | {
                      inventoryOrderId?: string;
                      branchOfficeId?: string;
                      distributionCenterId?: string;
                      isNewOrderFromCedis?: boolean;
                  }
                | undefined;

            if (state?.inventoryOrderId) {
                const order = await inventoryOrderService.getInventoryOrder(state.inventoryOrderId);
                setCurrentOrder(order);
                setOrderItems(order.items.map((item, index) => ({ ...item, id: `temp-${index}` })));
                setNote(order.note || '');
                setCashAmount(order.cashAmount || 0);
                setCreditCardAmount(order.creditCardAmount || 0);
                setBankTransferAmount(order.bankTransferAmount || 0);
                setIsEditing(true);
            } else if (state?.branchOfficeId && state?.distributionCenterId) {
                setIsEditing(false);
            } else if (state?.isNewOrderFromCedis) {
                setShowBranchSelector(true);
                setIsEditing(false);
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void loadData();
    }, [location.state]);

    /** Stock de la sucursal para el grid: se recarga si cambia la sucursal. */
    useEffect(() => {
        const branchOfficeId = showBranchSelector ? selectedBranchOfficeId : branchOfficeSelected?.id;
        if (!branchOfficeId) {
            setStockByInput(new Map());
            return;
        }
        branchOfficeInventoryService
            .getInventory(branchOfficeId)
            .then((inventory) => {
                const map = new Map<string, { totalUnits: number; unit: string }>();
                inventory.forEach((item) => {
                    if (!item.input?.id) {
                        return;
                    }
                    map.set(item.input.id, { totalUnits: getAvailableUnits(item), unit: item.input?.unit?.unit || '' });
                });
                setStockByInput(map);
            })
            .catch(() => setStockByInput(new Map()));
        // eslint-disable-next-line
    }, [selectedBranchOfficeId, branchOfficeSelected?.id, showBranchSelector]);

    const handleAddItem = (input: Input) => {
        const existingItem = orderItems.find((item) => item.input.id === input.id);
        if (existingItem) {
            setOrderItems(
                orderItems.map((item) => (item.input.id === input.id ? { ...item, quantity: item.quantity + 1 } : item))
            );
        } else {
            setOrderItems([...orderItems, { input, quantity: 1, id: `temp-${Date.now()}` }]);
        }
    };

    const handleRemoveItem = (inputId?: string) => {
        const existingItem = orderItems.find((item) => item.input.id === inputId);
        if (existingItem && existingItem.quantity > 1) {
            setOrderItems(
                orderItems.map((item) => (item.input.id === inputId ? { ...item, quantity: item.quantity - 1 } : item))
            );
        } else {
            setOrderItems(orderItems.filter((item) => item.input.id !== inputId));
        }
    };

    const getTotal = () => {
        return orderItems.reduce((total, item) => total + item.quantity * item.input.costOfSale, 0);
    };

    const canModify = !isEditing || currentOrder?.status === InventoryOrderStatus.requested;

    const handleSaveOrder = async () => {
        if (orderItems.length === 0) {
            showAlertMessage('Agrega al menos un insumo al pedido');
            return;
        }

        showLoader();
        try {
            const branchOfficeId =
                currentOrder?.branchOfficeId ||
                (showBranchSelector ? selectedBranchOfficeId : branchOfficeSelected?.id);
            const distributionCenterId =
                currentOrder?.distributionCenterId ||
                distributionCenterSelected?.id ||
                branchOfficeSelected?.distributionCenterId;

            if (!branchOfficeId || !distributionCenterId) {
                showAlertMessage('Selecciona una sucursal antes de enviar el pedido');
                hideLoader();
                return;
            }

            const items = orderItems.map(({ id, ...item }) => item);

            if (currentOrder?.id) {
                await inventoryOrderService.updateInventoryOrderItems(currentOrder.id, items);
                // Si tienes un método específico para actualizar la nota, inclúyelo aquí
                showAlertMessage('Pedido actualizado exitosamente');
            } else {
                // Folio con seq ATÓMICO (dos dispositivos pidiendo a la vez ya no
                // repiten) y con la sucursal REAL del pedido: antes el prefijo usaba
                // branchOfficeSelected aunque el admin pidiera para otra sucursal.
                // Semilla con UNA lectura (antes se cargaban TODOS los pedidos de
                // insumos de la sucursal, sin fecha, en cada pedido nuevo).
                const orderBranchOffice = branchOffices?.find((branch) => branch.id === branchOfficeId);
                const shortId = await reserveShortId({
                    scope: 'inventory-orders',
                    branchOfficeId,
                    branchNumber: orderBranchOffice?.number || branchOfficeSelected?.number,
                    date: new Date()
                });

                const currentUser = await UserServ.getUser();
                await inventoryOrderService.saveInventoryOrder({
                    shortId,
                    status: InventoryOrderStatus.requested,
                    branchOfficeId,
                    distributionCenterId,
                    items,
                    note,
                    createdAt: new Date(),
                    paymentStatus: 'pending',
                    createdBy: currentUser
                });
                showAlertMessage('Pedido creado exitosamente');
            }
            navigation(-1);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const handleDeleteOrder = () => {
        if (!currentOrder?.id) return;
        dispatch(
            setConfirmDialog({
                open: true,
                title: '¿Eliminar pedido?',
                message: 'Esta acción no se puede deshacer. ¿Estás seguro de que deseas eliminar este pedido?',
                buttons: [
                    {
                        text: 'Eliminar pedido',
                        onPress: executeCancelOrder,
                        style: 'destructive'
                    }
                ]
            })
        );
    };

    const executeCancelOrder = async () => {
        if (!currentOrder?.id) return;
        showLoader();
        try {
            await inventoryOrderService.deleteInventoryOrder(currentOrder.id);
            showAlertMessage('Pedido cancelado exitosamente');
            navigation(-1);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const total = getTotal();

    const handlePayment = () => {
        void onConfirmPayment();
    };

    const onConfirmPayment = async () => {
        if (!currentOrder?.id) return;
        showLoader();
        try {
            await inventoryOrderService.payInventoryOrder(
                currentOrder.id,
                total,
                total,
                cashAmount,
                creditCardAmount,
                bankTransferAmount
            );
            showAlertMessage('Pago registrado exitosamente');
            const updatedOrder = await inventoryOrderService.getInventoryOrder(currentOrder.id);
            setCurrentOrder(updatedOrder);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const handleMarkAsInRoute = async () => {
        if (!currentOrder?.id) return;
        showLoader();
        try {
            await inventoryOrderService.updateStatus(currentOrder.id, InventoryOrderStatus.inRoute);
            showAlertMessage('Pedido marcado como "En ruta" exitosamente');
            const updatedOrder = await inventoryOrderService.getInventoryOrder(currentOrder.id);
            setCurrentOrder(updatedOrder);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const handleMarkAsReceived = async () => {
        if (!currentOrder?.id) return;
        showLoader();
        try {
            // La recepción entra al inventario ANTES de marcar el pedido: si falla,
            // el pedido NO queda en "Recibido" y el botón sigue aquí para reintentar.
            const warnings = await inventoryOrderService.updateStatus(currentOrder.id, InventoryOrderStatus.received);
            if (warnings.length > 0) {
                // La sucursal SÍ recibió; el aviso es sobre el CEDIS (§4.2)
                showAlertWarning(`Recibido, con avisos: ${warnings.join(' · ')}`);
            } else {
                showAlertMessage('Pedido marcado como "Recibido" exitosamente');
            }
            const updatedOrder = await inventoryOrderService.getInventoryOrder(currentOrder.id);
            setCurrentOrder(updatedOrder);
            navigation(-1);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    return (
        <Stack height="100%">
            <Grid container height="100%">
                <Grid
                    item
                    xs={12}
                    md={8}
                    height="100%"
                    sx={{
                        borderRight: '2px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <InputCategoriesTabs
                        enabled={true}
                        categoryIdSelected={selectedCategoryId}
                        onSelectCategoryId={setSelectedCategoryId}
                    />
                    <InputsGrid
                        inputs={inputs}
                        categoryIdSelected={selectedCategoryId}
                        canModify={canModify}
                        onAddItem={handleAddItem}
                        stockByInput={stockByInput}
                    />
                </Grid>

                <Grid item xs={12} md={4} height="100%">
                    <Stack height="100%">
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Button onClick={() => {}}>Resumen del Pedido</Button>
                            {(currentOrder?.status === InventoryOrderStatus.requested ||
                                currentOrder?.status === InventoryOrderStatus.canceled) && (
                                <Button color="error" onClick={handleDeleteOrder}>
                                    Eliminar
                                </Button>
                            )}
                        </Stack>

                        <Divider />

                        <InventoryCartList
                            orderItems={orderItems}
                            canModify={canModify}
                            onAddItem={handleAddItem}
                            onRemoveItem={handleRemoveItem}
                        />

                        <Divider />
                        <Box p={2}>
                            <InventoryOrderActions
                                note={note}
                                setNote={setNote}
                                canModify={canModify}
                                total={total}
                                cashAmount={cashAmount}
                                creditCardAmount={creditCardAmount}
                                bankTransferAmount={bankTransferAmount}
                                currentOrder={currentOrder}
                                onSaveOrder={handleSaveOrder}
                                onPayment={handlePayment}
                                onPayWithCash={() => modalCashCalculator.toggle()}
                                onPayWithCard={() => modalCreditCardCalculator.toggle()}
                                onPayWithTransfer={() => modalBankTransferCalculator.toggle()}
                                onRemoveCash={() => setCashAmount(0)}
                                onRemoveCard={() => setCreditCardAmount(0)}
                                onRemoveTransfer={() => setBankTransferAmount(0)}
                                onMarkAsInRoute={handleMarkAsInRoute}
                                onMarkAsReceived={handleMarkAsReceived}
                                showBranchSelector={showBranchSelector}
                                branchOffices={branchOffices}
                                selectedBranchOfficeId={selectedBranchOfficeId}
                                onBranchOfficeChange={setSelectedBranchOfficeId}
                            />
                        </Box>
                    </Stack>
                </Grid>
            </Grid>

            <ModalCreditCardCalculator
                modalCreditCardCalculator={modalCashCalculator}
                total={Math.max(0, total - creditCardAmount - bankTransferAmount)}
                onAddCreditCardAmount={(amount: number) =>
                    setCashAmount(Math.min(amount, total - creditCardAmount - bankTransferAmount))
                }
            />
            <ModalCreditCardCalculator
                modalCreditCardCalculator={modalCreditCardCalculator}
                total={Math.max(0, total - cashAmount - bankTransferAmount)}
                onAddCreditCardAmount={(amount: number) =>
                    setCreditCardAmount(Math.min(amount, total - cashAmount - bankTransferAmount))
                }
            />
            <ModalCreditCardCalculator
                modalCreditCardCalculator={modalBankTransferCalculator}
                total={Math.max(0, total - cashAmount - creditCardAmount)}
                onAddCreditCardAmount={(amount: number) =>
                    setBankTransferAmount(Math.min(amount, total - cashAmount - creditCardAmount))
                }
            />
        </Stack>
    );
};

export default InventoryPOS;
